import {
  getRawScoringFactorByColumnId,
  IMoosaSarConfigTag,
  IScoreMap,
  DatasetRowId,
  getSubstanceScoreValue,
  ColumnId,
  ITableDataRowMap,
} from '@discngine/moosa-models';
import clamp from 'lodash/clamp';

import { getColorFromGradient, rgbToString } from './colorUtils';

const NO_COLOR = 'none';

interface TagToColorParams {
  dataset: ITableDataRowMap;
  scores: IScoreMap;
  columnIndexes: Record<ColumnId, number>;
  rowId: DatasetRowId;
  tag: IMoosaSarConfigTag;
}

export const tagToColor = ({
  dataset,
  scores,
  columnIndexes,
  rowId,
  tag,
}: TagToColorParams): string => {
  if (!tag.isColored) {
    return NO_COLOR;
  }

  // Scoring Column
  if (tag.isScoringColumn) {
    const score = getSubstanceScoreValue(scores, rowId);

    return score == null
      ? NO_COLOR
      : rgbToString(getColorFromGradient(score, tag.gradient.scoredGradient));
  }

  // Scoring Colorizing Mode - works the same for numeric and discrete columns
  if (tag.isScoredColorizingMode) {
    const factor = getRawScoringFactorByColumnId(
      scores,
      columnIndexes,
      rowId,
      tag.columnId
    );

    return factor == null
      ? NO_COLOR
      : rgbToString(getColorFromGradient(factor, tag.gradient.scoredGradient));
  }

  const propertyValue = dataset[rowId]?.[tag.columnId];

  // Discrete Column, not Scoring Colorizing Mode
  if (tag.isDiscreteProperty) {
    const paletteValue = tag.discretePropertyValues.find((obj) => {
      switch (typeof propertyValue) {
        case 'number':
          return propertyValue === Number(obj.value);
        case 'string':
          return propertyValue === String(obj.value);
        default:
          return false;
      }
    });
    const tagColor = paletteValue?.color;

    return tagColor ? rgbToString(tagColor) : NO_COLOR;
  }

  // The case of common numeric property
  const value = Number(propertyValue);

  if (isNaN(value)) {
    return NO_COLOR;
  }

  const divider = tag.range.max - tag.range.min || 1;
  const positionInGradient = clamp((value - tag.range.min) / divider, 0, 1);

  return rgbToString(
    getColorFromGradient(positionInGradient, tag.gradient.datasetGradient)
  );
};
